import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
interface ParticleFieldProps {
  type: 'fire' | 'earth' | 'air' | 'water' | 'electric' | 'smoke' | 'light' | 'cosmic';
  color?: string;
  density?: number;
  className?: string;
}
function detectIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');
}
function detectReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
export function ParticleField({
  type,
  color,
  density = 30,
  className = ''
}: ParticleFieldProps) {
  // Defaults assume mobile-first to avoid heavy first render on phones.
  const [isMobile, setIsMobile] = useState<boolean>(true);
  const [reducedMotion, setReducedMotion] = useState<boolean>(false);
  useEffect(() => {
    setIsMobile(detectIsMobile());
    setReducedMotion(detectReducedMotion());
    const onResize = () => setIsMobile(detectIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const particles = useMemo(() => {
    if (reducedMotion) return [];
    // Significantly reduce density on mobile to avoid GPU/CPU strain
    const effectiveDensity = isMobile ? Math.max(4, Math.min(10, Math.floor(density * 0.25))) : density;
    return Array.from({
      length: effectiveDensity
    }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5
    }));
  }, [density, isMobile, reducedMotion]);
  const getParticleStyle = (type: string) => {
    // On mobile, drop CSS blur filters — they are GPU-expensive and can cause crashes
    // on lower-end devices, especially when combined with many animated layers.
    const blur = isMobile ? 'none' : undefined;
    switch (type) {
      case 'fire':
        return {
          backgroundColor: color || '#FF6B35',
          filter: blur ?? 'blur(2px)'
        };
      case 'earth':
        return {
          backgroundColor: color || '#D4AF37',
          filter: blur ?? 'blur(1px)'
        };
      case 'air':
        return {
          backgroundColor: color || '#C0C0C0',
          filter: blur ?? 'blur(3px)',
          opacity: 0.4
        };
      case 'water':
        return {
          backgroundColor: color || '#40E0D0',
          filter: blur ?? 'blur(2px)',
          opacity: 0.6
        };
      case 'electric':
        return {
          backgroundColor: color || '#00BFFF',
          filter: blur ?? 'blur(1px)'
        };
      case 'smoke':
        return {
          backgroundColor: color || '#8B008B',
          filter: blur ?? 'blur(4px)',
          opacity: 0.3
        };
      case 'light':
        return {
          backgroundColor: color || '#FFD700',
          filter: blur ?? 'blur(2px)'
        };
      case 'cosmic':
      default:
        return {
          backgroundColor: color || '#C9A84C',
          filter: blur ?? 'blur(2px)',
          opacity: 0.5
        };
    }
  };
  const getAnimation = (type: string, particle: {
    id: number;
  }) => {
    const baseY = ['0vh', '-100vh'];
    const baseX = [0, Math.random() * 100 - 50];
    switch (type) {
      case 'fire':
        return {
          y: baseY,
          x: [0, Math.random() * 30 - 15, Math.random() * 30 - 15],
          opacity: [0, 1, 1, 0],
          scale: [1, 1.5, 0.5]
        };
      case 'electric':
        return {
          y: baseY,
          x: [0, Math.random() * 60 - 30, Math.random() * 60 - 30, 0],
          opacity: [0, 1, 0.8, 1, 0]
        };
      case 'water':
        return {
          y: baseY,
          x: [0, Math.sin(particle.id) * 20, -Math.sin(particle.id) * 20],
          opacity: [0, 0.8, 0.8, 0]
        };
      default:
        return {
          y: baseY,
          x: baseX,
          opacity: [0, 1, 1, 0]
        };
    }
  };
  if (particles.length === 0) return null;
  return <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true" data-id="element-233">
      {particles.map(particle => <motion.div key={particle.id} className="absolute rounded-full will-change-transform" style={{
      left: `${particle.x}%`,
      bottom: 0,
      width: `${particle.size}px`,
      height: `${particle.size}px`,
      ...getParticleStyle(type)
    }} animate={getAnimation(type, particle)} transition={{
      duration: particle.duration,
      delay: particle.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-234" />)}
    </div>;
}